(() => {
  if (customElements.get('mm-shipping-quote')) return;

  const TEMPORARY_PROPERTY = '_1000marcas_frete_simulacao';
  const DEFAULT_ERROR = 'Não conseguimos carregar as opções de entrega agora. Tente novamente em instantes.';

  class MMShippingQuote extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.form = this.querySelector('[data-mm-shipping-form]');
      this.zipInput = this.querySelector('[data-mm-shipping-zip]');
      this.button = this.querySelector('[data-mm-shipping-button]');
      this.status = this.querySelector('[data-mm-shipping-status]');
      this.results = this.querySelector('[data-mm-shipping-results]');

      if (!this.form || !this.zipInput || !this.button || !this.status || !this.results) return;

      this.originalButtonText = this.button.textContent.trim() || 'Ver frete e prazo';

      const savedZip = window.localStorage?.getItem('mm_shipping_zip') || '';
      if (/^\d{8}$/.test(savedZip)) this.zipInput.value = this.formatZip(savedZip);

      this.zipInput.addEventListener('input', () => {
        const digits = this.zipInput.value.replace(/\D/g, '').slice(0, 8);
        this.zipInput.value = this.formatZip(digits);
      });

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.quote();
      });
    }

    formatZip(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
      return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
    }

    getProductScope() {
      return this.closest('.product-template__content__container')
        || this.closest('product-info')
        || document.querySelector('.section-main-product')
        || document;
    }

    getVariantId() {
      const scope = this.getProductScope();
      const input = scope.querySelector(
        '.product-form__variant-id:not([disabled]), form[action*="/cart/add"] input[name="id"]:not([disabled]), form[data-type="add-to-cart-form"] input[name="id"]:not([disabled])'
      );
      const raw = input?.value || this.dataset.initialVariantId || '';
      const numeric = Number(String(raw).replace(/^gid:\/\/shopify\/ProductVariant\//, ''));
      return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
    }

    getQuantity() {
      const scope = this.getProductScope();
      const input = scope.querySelector('input[name="quantity"]');
      const quantity = Number.parseInt(input?.value || '1', 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getRoute(path) {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return `${normalizedRoot}${String(path).replace(/^\/+/, '')}`;
    }

    async fetchJson(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: { Accept: 'application/json', ...(options.headers || {}) }
      });

      let payload = null;
      try {
        payload = await response.json();
      } catch (_) {
        payload = null;
      }

      if (!response.ok) {
        const details = payload?.description || payload?.message || payload?.error;
        const message = Array.isArray(details) ? details.join(' ') : details;
        throw new Error(message || DEFAULT_ERROR);
      }

      return payload;
    }

    async lookupAddress(postalCode) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 3500);

      try {
        const response = await fetch(`https://viacep.com.br/ws/${postalCode}/json/`, {
          headers: { Accept: 'application/json' },
          signal: controller.signal
        });

        if (!response.ok) return null;
        const data = await response.json();
        if (data?.erro) return null;
        return data;
      } catch (_) {
        return null;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    createSimulationToken() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    async addTemporaryLine(variantId, quantity, token) {
      const payload = await this.fetchJson(this.getRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity,
            properties: { [TEMPORARY_PROPERTY]: token }
          }]
        })
      });

      const items = Array.isArray(payload?.items) ? payload.items : [payload].filter(Boolean);
      const matchingItem = items.find((item) => String(item?.properties?.[TEMPORARY_PROPERTY] || '') === token);
      return matchingItem?.key || '';
    }

    async getCart() {
      return this.fetchJson(this.getRoute('cart.js'));
    }

    async removeTemporaryLines(token, knownKey = '') {
      const keys = new Set();
      if (knownKey) keys.add(knownKey);

      const cart = await this.getCart();
      for (const item of cart?.items || []) {
        if (String(item?.properties?.[TEMPORARY_PROPERTY] || '') === token && item.key) {
          keys.add(item.key);
        }
      }

      for (const key of keys) {
        await this.fetchJson(this.getRoute('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
      }
    }

    buildShippingParams(postalCode, address) {
      const params = new URLSearchParams();
      params.set('shipping_address[country]', 'BR');
      params.set('shipping_address[zip]', postalCode);
      if (address?.uf) params.set('shipping_address[province]', address.uf);
      if (address?.localidade) params.set('shipping_address[city]', address.localidade);
      return params;
    }

    async prepareShippingRates(params) {
      const response = await fetch(this.getRoute('cart/prepare_shipping_rates.json'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: params.toString()
      });

      if (!response.ok && response.status !== 202) {
        let payload = null;
        try {
          payload = await response.json();
        } catch (_) {
          payload = null;
        }

        const details = payload?.description || payload?.message || payload?.error;
        const message = Array.isArray(details) ? details.join(' ') : details;
        throw new Error(message || 'Não foi possível iniciar o cálculo do frete.');
      }
    }

    async pollShippingRates(params) {
      for (let attempt = 0; attempt < 15; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 550 : 850));

        const response = await fetch(`${this.getRoute('cart/async_shipping_rates.json')}?${params.toString()}`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (response.status === 202) continue;

        let payload = null;
        try {
          payload = await response.json();
        } catch (_) {
          payload = null;
        }

        if (!response.ok) {
          const details = payload?.description || payload?.message || payload?.error;
          const message = Array.isArray(details) ? details.join(' ') : details;
          throw new Error(message || DEFAULT_ERROR);
        }

        if (payload && Array.isArray(payload.shipping_rates)) return payload.shipping_rates;
      }

      throw new Error('O cálculo de frete demorou mais do que o esperado. Tente novamente.');
    }

    setLoading(isLoading) {
      this.button.disabled = isLoading;
      this.zipInput.disabled = isLoading;
      this.form.setAttribute('aria-busy', String(isLoading));
      this.button.textContent = isLoading ? 'Buscando melhores opções...' : this.originalButtonText;
    }

    showStatus(message, isError = false) {
      this.status.textContent = message || '';
      this.status.classList.toggle('mm-shipping__status--error', isError);
    }

    clearResults() {
      this.results.replaceChildren();
      this.results.hidden = true;
    }

    formatMoney(value) {
      const numeric = Number.parseFloat(String(value ?? '0').replace(',', '.'));
      if (!Number.isFinite(numeric)) return String(value ?? '');
      if (numeric === 0) return 'Grátis';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
    }

    addBusinessDays(date, days) {
      const result = new Date(date);
      let remaining = Math.max(0, Number(days) || 0);

      while (remaining > 0) {
        result.setDate(result.getDate() + 1);
        const day = result.getDay();
        if (day !== 0 && day !== 6) remaining -= 1;
      }

      return result;
    }

    formatDate(date) {
      return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date);
    }

    getDeliveryText(rate) {
      const range = rate?.delivery_range || {};
      const minValue = Number(range.min ?? rate?.min_delivery_days ?? rate?.delivery_days);
      const maxValue = Number(range.max ?? rate?.max_delivery_days ?? rate?.delivery_days);

      if (Number.isFinite(minValue) || Number.isFinite(maxValue)) {
        const minimum = Number.isFinite(minValue) ? minValue : maxValue;
        const maximum = Number.isFinite(maxValue) ? maxValue : minimum;
        const start = this.addBusinessDays(new Date(), minimum);
        const end = this.addBusinessDays(new Date(), maximum);
        return minimum === maximum
          ? `Previsão: ${this.formatDate(start)}`
          : `Previsão: de ${this.formatDate(start)} a ${this.formatDate(end)}`;
      }

      if (Array.isArray(rate?.delivery_days) && rate.delivery_days.length) {
        const validDays = rate.delivery_days.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
        if (validDays.length) {
          const lastDay = validDays[validDays.length - 1];
          return lastDay === 1
            ? 'Entrega em 1 dia útil após o envio do produto'
            : `Entrega em ${lastDay} dias úteis após o envio do produto`;
        }
      }

      return '';
    }

    renderOptions(rates, address, zip) {
      this.clearResults();

      const unique = new Map();
      for (const rate of rates || []) {
        if (!rate) continue;
        const key = `${rate.name || rate.presentment_name || ''}|${rate.price ?? ''}`;
        if (!unique.has(key)) unique.set(key, rate);
      }

      const list = [...unique.values()].sort((a, b) => Number.parseFloat(a.price || 0) - Number.parseFloat(b.price || 0));
      if (!list.length) {
        this.showStatus('No momento, não encontramos opções de entrega para este CEP.', true);
        return;
      }

      const ul = document.createElement('ul');
      ul.className = 'mm-shipping__list';

      for (const rate of list) {
        const li = document.createElement('li');
        li.className = 'mm-shipping__option';

        const copy = document.createElement('div');
        copy.className = 'mm-shipping__option-copy';

        const title = document.createElement('span');
        title.className = 'mm-shipping__option-title';
        title.textContent = rate.presentment_name || rate.name || 'Entrega';
        copy.append(title);

        const deliveryText = this.getDeliveryText(rate);
        if (deliveryText) {
          const delivery = document.createElement('span');
          delivery.className = 'mm-shipping__option-delivery';
          delivery.textContent = deliveryText;
          copy.append(delivery);
        }

        const price = document.createElement('strong');
        price.className = 'mm-shipping__option-price';
        price.textContent = this.formatMoney(rate.price);

        li.append(copy, price);
        ul.append(li);
      }

      this.results.append(ul);
      this.results.hidden = false;

      const destination = address?.localidade && address?.uf
        ? `${address.localidade}/${address.uf}`
        : `CEP ${this.formatZip(zip)}`;
      this.showStatus(`${list.length} ${list.length === 1 ? 'opção encontrada' : 'opções encontradas'} para ${destination}.`);
    }

    async quote() {
      const zip = this.zipInput.value.replace(/\D/g, '');
      if (!/^\d{8}$/.test(zip)) {
        this.clearResults();
        this.showStatus('Digite um CEP válido no formato 00000-000.', true);
        this.zipInput.focus();
        return;
      }

      if (window.__mmShippingCalculatorBusy) {
        this.showStatus('Aguarde o cálculo em andamento e tente novamente.', true);
        return;
      }

      const variantId = this.getVariantId();
      if (!variantId) {
        this.clearResults();
        this.showStatus('Selecione uma variação do produto antes de calcular o frete.', true);
        return;
      }

      const quantity = this.getQuantity();
      window.localStorage?.setItem('mm_shipping_zip', zip);
      window.__mmShippingCalculatorBusy = true;
      this.clearResults();
      this.showStatus('Consultando opções de entrega...');
      this.setLoading(true);

      const address = await this.lookupAddress(zip);
      const token = this.createSimulationToken();
      let temporaryKey = '';
      let rates = null;
      let calculationError = null;
      let cleanupError = null;

      try {
        temporaryKey = await this.addTemporaryLine(variantId, quantity, token);
        const params = this.buildShippingParams(zip, address);
        await this.prepareShippingRates(params);
        rates = await this.pollShippingRates(params);
      } catch (error) {
        calculationError = error;
      } finally {
        try {
          await this.removeTemporaryLines(token, temporaryKey);
        } catch (error) {
          cleanupError = error;
          console.error('[1000 Marcas] Falha ao remover item temporário da simulação:', error);
        }

        window.__mmShippingCalculatorBusy = false;
        this.setLoading(false);
      }

      if (cleanupError) {
        this.clearResults();
        this.showStatus('Não foi possível finalizar a simulação com segurança. Atualize a página e confira o carrinho antes de continuar.', true);
        return;
      }

      if (calculationError) {
        console.error('[1000 Marcas] Erro no cálculo de frete:', calculationError);
        this.clearResults();
        this.showStatus(calculationError.message || DEFAULT_ERROR, true);
        return;
      }

      this.renderOptions(rates || [], address, zip);
    }
  }

  customElements.define('mm-shipping-quote', MMShippingQuote);
})();
